import { useEffect, useRef } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import { useSeriesScrollPosition } from '../../../hooks/useSeriesScrollPosition';

const StyledContainer = styled(Box)(
  ({ theme }) => ({
    borderStyle: 'solid',
    borderColor: theme.palette.focus90.main,
    borderWidth: '1px 3px 3px 1px',
    borderRadius: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',

    width: '16.5rem',
    minWidth: '16.5rem',

    [theme.breakpoints.up('md')]: {
      width: '20.5rem',
      minWidth: '20.5rem'
    }
  })
);

const StyledImageContainer = styled(SharedAsset)(({ theme }) => ({
  borderRadius: '0.75rem 0.75rem 0 0',
  borderBottom: `1px solid ${theme.palette.focus90.main}`,
  width: '100%'
}));

const StyledInfoContainer = styled(Box)({
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%'
});

const StyledTitle = styled(Text)({
  marginBottom: '1.5rem'
});

const StyledButton = styled(Button)({
  maxWidth: '4.6875rem',
  alignSelf: 'flex-end',
  '&:before': {
    content: '""',
    cursor: 'pointer',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  },
  position: 'static'
});

const EDU_CARD_SELECTED = 'edu_card_selected';

export const ArticleCard = ({
  name, thumbnailUrl, seriesKey, articleKey, focusCard
}) => {
  const { businessEntityUuid } = useBusinessEntity();
  const navigate = useNavigate();
  const cardRef = useRef();

  const {
    saveSelectedCard,
    focusSelectedCard
  } = useSeriesScrollPosition(cardRef, EDU_CARD_SELECTED, seriesKey, articleKey);

  const trackArticleCardClick = useButtonPressedCallback({
    elementName: name,
    pageCategory: 'dashboard',
    pageSection: 'education'
  });

  const handleClick = (event) => {
    saveSelectedCard();
    trackArticleCardClick(event);
    navigate(`/d/company/${businessEntityUuid}/education-studio/${seriesKey}/${articleKey}`);
  };

  useEffect(() => {
    if (focusCard) focusSelectedCard();
  }, [focusCard, focusSelectedCard]);

  return (
    <StyledContainer ref={cardRef}>
      <StyledImageContainer
        alt={name}
        src={thumbnailUrl}
      />
      <StyledInfoContainer>
        <StyledTitle size="lg" bold>{name}</StyledTitle>
        <StyledButton
          variant="contained"
          color="primary"
          size="small"
          name="Read"
          onClick={handleClick}
        >
          Read
        </StyledButton>
      </StyledInfoContainer>
    </StyledContainer>
  );
};

ArticleCard.propTypes = {
  name: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string.isRequired,
  seriesKey: PropTypes.string.isRequired,
  articleKey: PropTypes.string.isRequired,
  focusCard: PropTypes.bool
};

ArticleCard.defaultProps = {
  focusCard: false
};
